import { useState } from 'react';

import { Button } from '../shared/ui/button';
import { Claim } from '../shared/ui/claim';
import { Checkbox } from '../shared/ui/checkbox';
import { CopyField } from '../shared/ui/copy-field';
import { FactList, FactRow } from '../shared/ui/fact-list';
import { FieldLabel } from '../shared/ui/field-label';
import { Hint } from '../shared/ui/hint';
import { Input } from '../shared/ui/input';
import { Kbd } from '../shared/ui/kbd';
import { Markers } from '../shared/ui/markers';
import { Meter, MeterRow } from '../shared/ui/meter';
import { Modal } from '../shared/ui/modal';
import { Note } from '../shared/ui/note';
import { OptionCard } from '../shared/ui/option-card';
import { SectionPanel } from '../shared/ui/section-panel';
import { Select } from '../shared/ui/select';
import { Sparkline } from '../shared/ui/sparkline';
import { StatusBadge } from '../shared/ui/status-badge';
import { Switch } from '../shared/ui/switch';
import { ShortId } from '../shared/ui/table';
import {
  Banner,
  Card,
  DataPanel,
  Page,
  PageHeader,
  Section,
  StateLabel,
} from '../shared/ui/layout';
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  LoadingState,
  NoMatchState,
} from '../shared/ui/states';
import { ApiError } from '../shared/api/errors';

const EVENT_STATUSES = [
  'successful',
  'queued',
  'delivering',
  'pending',
  'on_hold',
  'failed',
  'error',
  'filtered',
  'dropped',
  'paused',
];

const ATTEMPT_STATES = ['scheduled', 'in_flight', 'succeeded', 'failed', 'unknown', 'abandoned'];

const SOURCE_STATES = ['active', 'disabled'];
const SECRET_STATES = ['active', 'grace', 'expired'];

const SPARK = [0, 0, 1, 0, 3, 7, 12, 9, 2, 0, 0, 1, 4, 11, 18, 14, 6, 1, 0, 0, 2, 5, 3, 0];

const SURFACES = [
  ['surface', 'the ground - belongs to nobody'],
  ['field', 'lighter than the ground - you can type here'],
  ['surface-sunken', 'darker than the ground - came from the server'],
  ['surface-raised', 'menus and the palette - things you act on'],
];

const TEXTS = [
  ['primary', 'headings, values, anything read'],
  ['muted', 'labels, hints, secondary'],
  ['faint', 'placeholders, disabled, mono meta'],
];

const sampleError = new ApiError(
  {
    code: 'VALIDATION',
    httpCode: 400,
    requestId: 'req_2vQ1mAf7ULkH',
    timestamp: new Date().toISOString(),
    path: '/v1/destinations',
    method: 'POST',
    message: 'The destination was not saved',
    details: [
      { field: 'config.url', message: 'needs an absolute https address' },
      { field: 'timeoutMs', message: 'no more than 30000' },
    ],
  },
  400,
);

const Swatch = ({ token, role }: { token: string; role: string }) => (
  <div className="flex items-center gap-3">
    <span
      className="size-8 shrink-0 rounded-control"
      style={{ background: `var(--color-${token})` }}
    />
    <div>
      <div className="font-mono text-data text-primary">{token}</div>
      <div className="text-meta text-muted">{role}</div>
    </div>
  </div>
);

const TextSwatch = ({ token, role }: { token: string; role: string }) => (
  <div className="flex items-center gap-3">
    <span
      className="flex size-8 shrink-0 items-center justify-center rounded-control border border-border-raised text-meta"
      style={{ color: `var(--color-${token})` }}
    >
      Aa
    </span>
    <div>
      <div className="font-mono text-data text-primary">text-{token}</div>
      <div className="text-meta text-muted">{role}</div>
    </div>
  </div>
);

export const ReferencePage = () => {
  const [signing, setSigning] = useState(true);
  const [postOn, setPostOn] = useState(true);
  const [readFrom, setReadFrom] = useState('body');
  const [columnOn, setColumnOn] = useState(false);
  const [choice, setChoice] = useState('shared');
  const [modal, setModal] = useState<null | 'sm' | 'md' | 'lg'>(null);

  return (
    <>
      <PageHeader
        title="Interface reference"
        description="Tokens, statuses and the four required states. If something has drifted, it shows here."
        actions={
          <>
            <Button variant="ghost" size="sm">
              Secondary
            </Button>
            <Button size="sm">Page action</Button>
          </>
        }
      />

      <Page>
        <Section
          title="Surfaces"
          description="Three levels, and each one means something. There is no chromatic colour in the chrome at all."
        >
          <Card>
            <div className="grid grid-cols-2 gap-6">
              <div className="flex flex-col gap-4">
                {SURFACES.map(([token, role]) => (
                  <Swatch key={token} token={token} role={role} />
                ))}
              </div>
              <div className="flex flex-col gap-4">
                {TEXTS.map(([token, role]) => (
                  <TextSwatch key={token} token={token} role={role} />
                ))}
              </div>
            </div>
          </Card>
        </Section>

        <Section title="Type" description="Ten steps and no intermediate values.">
          <Card>
            <div className="flex flex-col gap-4">
              <div className="text-display font-semibold tracking-display text-primary">
                Sign in · 30/36
              </div>
              <div className="text-title font-semibold tracking-title text-primary">
                Create account · 24/30
              </div>
              <div className="text-page font-semibold tracking-page text-primary">
                Events · 20/28
              </div>
              <div className="text-section font-semibold text-primary">Section heading · 15/22</div>
              <div className="text-body text-primary">Body text and fields · 14/20</div>
              <div className="font-mono text-data text-primary">
                evt_4MzAOUQklImCvBPt4R5Y · table data · 13/18 mono
              </div>
              <div className="text-meta text-muted">Labels, hints, units · 12/16</div>
              <div className="font-mono text-micro font-medium tracking-caps uppercase text-muted">
                State label · 11/16 mono caps
              </div>
              <div className="text-micro font-medium tracking-group text-faint">
                Group heading · 10/14 mono caps
              </div>
            </div>
          </Card>
        </Section>

        <Section
          title="Fields"
          description="Lighter than the ground means you can type here. Rejected is an outline, never a fill."
        >
          <Card>
            <div className="grid grid-cols-3 gap-6">
              <div className="flex flex-col gap-4">
                <StateLabel>Empty</StateLabel>
                <Input placeholder="pavel@rawnodes.dev" />
              </div>
              <div className="flex flex-col gap-4">
                <StateLabel>Rejected</StateLabel>
                <div>
                  <Input defaultValue="pavel@" invalid />
                  <p className="mt-label text-meta text-danger">This address has no domain</p>
                </div>
              </div>
              <div className="flex flex-col gap-4">
                <StateLabel>Locked</StateLabel>
                <div>
                  <Input defaultValue="dasha@rawnodes.dev" disabled />
                  <p className="mt-label text-meta text-muted">Value comes from the invitation</p>
                </div>
              </div>
            </div>
          </Card>
        </Section>

        <Section
          title="Buttons"
          description="One primary action per screen. Red belongs to delivery failures, so no button ever uses it."
        >
          <Card>
            <div className="grid grid-cols-4 gap-4">
              <Button block>Primary action</Button>
              <Button variant="ghost" block>
                Secondary action
              </Button>
              <Button block waiting waitingLabel="Waiting for the answer">
                Primary action
              </Button>
              <Button variant="ghost" block disabled>
                Unavailable
              </Button>
            </div>
          </Card>
        </Section>

        <Section
          title="Data and rejection"
          description="Darker than the ground: this came from the server and is not yours to edit."
        >
          <div className="grid grid-cols-2 gap-6">
            <DataPanel>
              <FactList ruled={false}>
                <FactRow label="Organization">RawNodes</FactRow>
                <FactRow label="Role">Developer - reads events, triggers retries</FactRow>
                <FactRow label="Expires" mono>
                  24 Sep 2026
                </FactRow>
              </FactList>
            </DataPanel>
            <Banner detail="504 · gateway timeout · 15:42:07">
              You are not signed in. Your password was never checked.
            </Banner>
          </div>
        </Section>

        <Section
          title="Event statuses"
          description="Human labels; the raw value appears only in the tooltip."
        >
          <Card>
            <div className="flex flex-col gap-3">
              {EVENT_STATUSES.map((value) => (
                <div key={value} className="grid grid-cols-2 items-center gap-4">
                  <StatusBadge
                    vocabulary="event"
                    value={value}
                    attemptCount={2}
                    maxAttempts={5}
                    nextAttemptAt={new Date(Date.now() + 4 * 60_000).toISOString()}
                  />
                  <span className="font-mono text-meta text-faint">{value}</span>
                </div>
              ))}
            </div>
          </Card>
        </Section>

        <Section
          title="Attempt states"
          description="The same word failed means something else here - the dictionary is keyed by the pair."
        >
          <Card>
            <div className="flex flex-col gap-3">
              {ATTEMPT_STATES.map((value) => (
                <div key={value} className="grid grid-cols-2 items-center gap-4">
                  <StatusBadge vocabulary="attempt" value={value} />
                  <span className="font-mono text-meta text-faint">{value}</span>
                </div>
              ))}
              <div className="grid grid-cols-2 items-center gap-4">
                <StatusBadge vocabulary="attempt" value="something_new" />
                <span className="text-meta text-faint">
                  unknown value - shown raw, never mapped to a neighbour
                </span>
              </div>
            </div>
          </Card>
        </Section>

        <Section title="Markers and keys" description="Not statuses: no fill, no colour, mono.">
          <Card>
            <div className="flex items-center gap-6">
              <StatusBadge vocabulary="event" value="successful" />
              <Markers origin="replay" generation={2} transformLoggedError bookmarked />
              <span className="flex items-center gap-2">
                <Kbd>⌘K</Kbd>
                <Kbd>esc</Kbd>
              </span>
            </div>
          </Card>
        </Section>

        <Section
          title="Hint"
          description="The designer's prose. Twelve boards of thirteen carry it and it is half of what the product means."
        >
          <Card>
            <Input placeholder="https://api.example.com/hooks" />
            <Hint>
              We post a sample to your own address and show you whether the signature checks out
            </Hint>
          </Card>
        </Section>

        <Section
          title="Facts"
          description="Label, value, rule. The value carries the meaning in its colour; the label never does."
        >
          <div className="grid grid-cols-2 gap-6">
            <Card>
              <StateLabel>Ruled — the shape seven boards draw</StateLabel>
              <FactList>
                <FactRow label="Events in this issue">204</FactRow>
                <FactRow label="Retries still available" tone="success" mono>
                  31
                </FactRow>
                <FactRow label="Retries already spent" tone="danger" mono>
                  168
                </FactRow>
                <FactRow label="Currently in flight" tone="motion" mono>
                  5
                </FactRow>
                <FactRow label="Skipped, were in flight" tone="muted" mono>
                  5
                </FactRow>
              </FactList>
            </Card>
            <Card>
              <StateLabel>Unruled — inside a data panel</StateLabel>
              <DataPanel>
                <FactList ruled={false}>
                  <FactRow label="Organization">RawNodes</FactRow>
                  <FactRow label="Role">Developer — reads events, triggers retries</FactRow>
                  <FactRow label="Expires" mono>
                    24 Sep 2026
                  </FactRow>
                </FactList>
              </DataPanel>
            </Card>
          </div>
        </Section>

        <Section
          title="Dialogs"
          description="Three widths and one scrim. A confirmation names the number it is about to act on."
        >
          <Card>
            <div className="flex items-center gap-3">
              <Button size="sm" variant="ghost" onClick={() => setModal('sm')}>
                460 — confirm
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setModal('md')}>
                480 — a short table
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setModal('lg')}>
                520 — table and prose
              </Button>
            </div>
            <Modal
              open={modal !== null}
              onOpenChange={(next) => !next && setModal(null)}
              width={modal ?? 'sm'}
              title="Retry all 204"
              description="Before anything runs, here is what it would touch."
              footerNote="199 will be sent"
              submit={{ label: 'Send 199', onSubmit: () => setModal(null) }}
            >
              <FactList>
                <FactRow label="Events in this issue">204</FactRow>
                <FactRow label="Retries already spent" tone="danger" mono>
                  168
                </FactRow>
                <FactRow label="Currently in flight" tone="motion" mono>
                  5
                </FactRow>
              </FactList>
              <Note className="mt-3">
                The 168 that spent their retries get a fresh delivery each. The 5 in flight are
                skipped — retrying them now would duplicate.
              </Note>
            </Modal>
          </Card>
        </Section>

        <Section
          title="Source and secret states"
          description="Keyed by the API value, never by the word on the board: `revoked` in this API belongs to a different entity."
        >
          <div className="grid grid-cols-2 gap-6">
            <Card>
              <StateLabel>source</StateLabel>
              <div className="flex flex-col gap-3">
                {SOURCE_STATES.map((value) => (
                  <div key={value} className="grid grid-cols-2 items-center gap-4">
                    <StatusBadge vocabulary="source" value={value} />
                    <span className="font-mono text-meta text-faint">{value}</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card>
              <StateLabel>secret</StateLabel>
              <div className="flex flex-col gap-3">
                {SECRET_STATES.map((value) => (
                  <div key={value} className="grid grid-cols-2 items-center gap-4">
                    <StatusBadge vocabulary="secret" value={value} />
                    <span className="font-mono text-meta text-faint">{value}</span>
                  </div>
                ))}
                <div className="grid grid-cols-2 items-center gap-4">
                  <StatusBadge vocabulary="secret" value="revoked" />
                  <span className="text-meta text-faint">
                    not a secret state — shown raw rather than guessed
                  </span>
                </div>
              </div>
            </Card>
          </div>
        </Section>

        <Section
          title="Notes"
          description="The consequence of the action in front of you. Not a Banner: that is the server refusing a form."
        >
          <div className="grid grid-cols-2 gap-6">
            <Note>
              Stopping keeps what already went. Deliveries already handed to the queue will arrive.
            </Note>
            <Note tone="danger">
              Disabling the source makes it answer 410 Gone. The sender is told to stop, and nothing
              is queued while it is off.
            </Note>
          </div>
        </Section>

        <Section
          title="Section panel"
          description="A bordered box with a 36px head let into it. `Section` divides a page; this holds a table."
        >
          <SectionPanel
            title="Signing secrets"
            note="rotated 3 Sep"
            actions={
              <Button size="sm" variant="ghost">
                Rotate
              </Button>
            }
          >
            <div className="flex flex-col">
              <div className="flex h-row-lg items-center gap-inset border-b border-border-row px-3">
                <StatusBadge vocabulary="secret" value="active" />
                <span className="font-mono text-data text-primary">whsec_••••••••4f21</span>
                <span className="ml-auto font-mono text-micro text-faint">last used 15:18</span>
              </div>
              <div className="flex h-row-lg items-center gap-inset px-3">
                <StatusBadge vocabulary="secret" value="grace" />
                <span className="font-mono text-data text-primary">whsec_••••••••9ab0</span>
                <span className="ml-auto font-mono text-micro text-faint">
                  stops verifying 18:00
                </span>
              </div>
            </div>
          </SectionPanel>
        </Section>

        <Section
          title="Copy"
          description="The clipboard always takes the WHOLE value, even where the screen shows a shortened one."
        >
          <div className="grid grid-cols-2 gap-6">
            <CopyField
              label="Ingest URL"
              value="https://in.hookpipe.io/in/acme/stripe-prod-a91f2c"
            />
            <CopyField
              size="sm"
              value="evt_4MzAOUQklImCvBPt4R5Y"
              display={<ShortId id="evt_4MzAOUQklImCvBPt4R5Y" />}
            />
          </div>
        </Section>

        <Section
          title="Toggles and choices"
          description="On means on now, not saved. Every toggle carries the consequence beside it."
        >
          <div className="grid grid-cols-2 gap-6">
            <Card>
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={signing}
                    onCheckedChange={setSigning}
                    aria-label="Sign what we send"
                  />
                  <span className="text-data text-primary">30×17 · on</span>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={false} onCheckedChange={() => undefined} aria-label="Off" />
                  <span className="text-data text-primary">30×17 · off</span>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    size="sm"
                    checked={columnOn}
                    onCheckedChange={setColumnOn}
                    aria-label="Show column"
                  />
                  <span className="text-data text-primary">28×16 · list of columns</span>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked
                    disabled
                    onCheckedChange={() => undefined}
                    aria-label="Locked on"
                  />
                  <span className="text-data text-primary">locked</span>
                </div>
                <Checkbox checked={postOn} onCheckedChange={setPostOn} label="POST" />
                <Checkbox checked={false} onCheckedChange={() => undefined} label="PUT" />
                <Checkbox checked disabled onCheckedChange={() => undefined} label="GET (locked)" />
                <div>
                  <FieldLabel htmlFor="ref-select">Read the type from</FieldLabel>
                  <Select
                    id="ref-select"
                    value={readFrom}
                    onChange={setReadFrom}
                    options={[
                      { value: 'body', label: 'the body', description: 'A path into the JSON.' },
                      {
                        value: 'header',
                        label: 'a header',
                        description: 'The sender names the type on the request itself.',
                      },
                    ]}
                  />
                </div>
              </div>
            </Card>
            <div className="flex flex-col gap-3">
              <OptionCard
                as="label"
                selected={choice === 'shared'}
                control={
                  <input
                    type="radio"
                    className="accent-accent"
                    name="reference-choice"
                    checked={choice === 'shared'}
                    onChange={() => setChoice('shared')}
                  />
                }
                title="One address for everyone"
                description="Every sender posts to the same URL. Simplest, and you cannot tell two senders apart."
              />
              <OptionCard
                as="label"
                selected={choice === 'per'}
                control={
                  <input
                    type="radio"
                    className="accent-accent"
                    name="reference-choice"
                    checked={choice === 'per'}
                    onChange={() => setChoice('per')}
                  />
                }
                title="An address per customer"
                description="Each customer gets their own URL and their own secret."
              />
              <OptionCard
                control={
                  <Switch
                    checked={signing}
                    onCheckedChange={setSigning}
                    aria-label="Sign what we send"
                  />
                }
                selected={signing}
                title="Sign what we send"
                description="We add a signature header so the far side can prove the body is ours."
              >
                <Input placeholder="whsec_…" />
              </OptionCard>
            </div>
          </div>
        </Section>

        <Section
          title="Histograms"
          description="One colour, no interaction. A zero bucket is a 1px line: a gap would read as no data."
        >
          <Card>
            <div className="flex flex-col gap-6">
              <div>
                <StateLabel>22 · in a list row</StateLabel>
                <div className="w-col-spark">
                  <Sparkline
                    values={SPARK}
                    tone="danger"
                    height="spark"
                    gap={1}
                    label="Failures in the last 6 hours"
                  />
                </div>
              </div>
              <div>
                <StateLabel>44 · on a state card</StateLabel>
                <Sparkline
                  values={SPARK.slice(0, 20)}
                  tone="muted"
                  height="card"
                  gap={2}
                  label="Failures per 15 minutes"
                />
              </div>
              <div>
                <StateLabel>96 · the figure of a screen</StateLabel>
                <Sparkline
                  values={SPARK}
                  tone="motion"
                  height="chart"
                  gap={3}
                  label="Failures per 15 minutes"
                />
              </div>
              <div>
                <StateLabel>All zero — still a row of lines, never blank</StateLabel>
                <Sparkline
                  values={[0, 0, 0, 0, 0, 0, 0, 0]}
                  tone="danger"
                  height="spark"
                  gap={1}
                  label="Nothing failed"
                />
              </div>
            </div>
          </Card>
        </Section>

        <Section
          title="Meters"
          description="Where things stand. The bars share a left and a right edge whatever their labels say."
        >
          <div className="grid grid-cols-2 gap-6">
            <Card>
              <StateLabel>A run in progress</StateLabel>
              <Meter value={46} />
              <div className="mt-2 flex justify-between">
                <span className="font-mono text-micro text-faint">92 sent</span>
                <span className="font-mono text-micro text-faint">about 40 seconds left</span>
              </div>
            </Card>
            <Card>
              <StateLabel>Where the 204 stand right now</StateLabel>
              <MeterRow tone="danger" label="Retries spent" value={82} count="168" />
              <MeterRow tone="motion" label="Retry pending" value={15} count="31" />
              <MeterRow tone="motion" label="Delivering" value={3} count="5" />
              <MeterRow tone="success" label="Delivered" value={100} count="204" />
            </Card>
          </div>
        </Section>

        <Section
          title="Claims"
          description="One sentence the product stands behind, and what that sentence rests on."
        >
          <div className="flex flex-col gap-4">
            <Claim footnote="Counted over 24 hours · as of 15:35">
              Stripe production has been answering 502 since 15:18. 204 events are waiting and none
              of them has been delivered.
            </Claim>
            <div className="grid grid-cols-3 gap-4">
              <Claim size="card">
                Quiet for 8 minutes. It closes itself once the failure stops.
              </Claim>
              <Claim size="card">
                Closed at 15:46 because it stopped recurring, not by a person.
              </Claim>
              <Claim size="card">Failing again after a quiet spell, first seen 16:01.</Claim>
            </div>
          </div>
        </Section>

        <Section title="The four required states">
          <div className="flex flex-col gap-4">
            <EmptyState
              title="No events yet"
              description="The source exists, but nothing has ever knocked on it."
              action={
                <span className="font-mono text-data text-primary">
                  https://hooks.hookpipe.dev/in/rawnodes-a91f2c/stripe
                </span>
              }
            />
            <NoMatchState onReset={() => undefined} />
            <LoadingState rows={4} />
            <ErrorState error={sampleError} onRetry={() => undefined} />
            <ForbiddenState needs="owner" />
          </div>
        </Section>
      </Page>
    </>
  );
};
